import { z } from 'zod';
import { Stack } from '@mui/system';
import { captureException } from '@sentry/react';
import { KeyboardArrowDown } from '@mui/icons-material';
import React, { useState, useEffect, useRef } from 'react';
import { onSnapshot, type Timestamp, doc, setDoc } from '@firebase/firestore';
import { Card, CardContent, Typography, IconButton, Collapse, useTheme, alpha } from '@mui/material';

import { db } from '../../firebase-config';
import { useAuth } from '../../hooks/useAuth';

const DEBOUNCE_TIME = 300; // 300ms debounce time

type BannerProps = Omit<React.ComponentProps<typeof Card>, 'onClick'> & {
  title: string;
  description: string;
  bannerId?: string;
  colour?: 'info' | 'warning' | 'error' | 'success';
  nonDismissable?: boolean;
  updatedAt?: Timestamp;
};

// Define Zod schema for banner state validation
const bannerStateSchema = z.object({
  action: z.enum(['MINIMISED', 'DISMISSED', 'EXPANDED']),
  actionedAt: z.number().optional(),
});

const BANNER_STATE_ACTION = {
  MINIMISED: 'MINIMISED',
  DISMISSED: 'DISMISSED',
  EXPANDED: 'EXPANDED',
} as const;

const DEFAULT_BANNER_STATE = {
  action: BANNER_STATE_ACTION.MINIMISED,
} satisfies z.infer<typeof bannerStateSchema>;

const getBannerState = async ({ data }: { data: unknown }) => {
  if (!data) {
    return DEFAULT_BANNER_STATE;
  }

  try {
    const validatedData = bannerStateSchema.safeParse(data);

    if (validatedData.success) {
      return validatedData.data;
    }

    return DEFAULT_BANNER_STATE;
  } catch (error) {
    captureException(error, {
      tags: {
        issueIn: 'Banner.getBannerState',
      },
      extra: {
        data,
      },
    });

    return DEFAULT_BANNER_STATE;
  }
};

type SaveBannerStateParams = {
  userId: string;
  bannerId: string;
  state: z.infer<typeof bannerStateSchema>;
};

const saveBannerState = async ({ userId, bannerId, state }: SaveBannerStateParams) => {
  if (!userId || !bannerId) {
    return;
  }

  try {
    const bannerStateRef = doc(db, 'patients', userId, 'bannerStates', bannerId);

    await setDoc(bannerStateRef, {
      ...state,
      actionedAt: Date.now(),
    });
  } catch (error) {
    captureException(error, {
      tags: {
        issueIn: 'Banner.saveBannerState',
      },
      extra: {
        userId,
        bannerId,
      },
    });
  }
};

export const Banner = ({
  title,
  description,
  colour = 'info',
  bannerId,
  updatedAt,
  nonDismissable = false,
  sx,
  ...props
}: BannerProps) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [state, setState] = useState<z.infer<typeof bannerStateSchema>>(DEFAULT_BANNER_STATE);
  const isUserInitiatedRef = useRef(false);

  // Debounce state updates (but only for user-initiated changes)
  useEffect(() => {
    if (!isUserInitiatedRef.current) {
      return () => {};
    }

    const timer = setTimeout(() => {
      if (!user?.uid || !bannerId) {
        return;
      }

      saveBannerState({ bannerId, userId: user.uid, state: { action: state.action } });

      // Reset the flag after saving
      isUserInitiatedRef.current = false;
    }, DEBOUNCE_TIME);

    return () => clearTimeout(timer);
  }, [state.action, user?.uid, bannerId]);

  useEffect(() => {
    if (!bannerId || !user?.uid) {
      return () => {};
    }

    const unsubscribe = onSnapshot(doc(db, 'patients', user.uid, 'bannerStates', bannerId), (snapshot) => {
      getBannerState({ data: snapshot.data() }).then((bannerState) => {
        // This is a system-initiated state change, not user-initiated
        isUserInitiatedRef.current = false;
        setState(bannerState);
      });
    });

    return () => {
      unsubscribe();
    };
  }, [bannerId, user?.uid, updatedAt]);

  const handleToggleExpanded = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    e.stopPropagation();

    const newState = {
      ...state,
      action:
        state?.action === BANNER_STATE_ACTION.MINIMISED ? BANNER_STATE_ACTION.EXPANDED : BANNER_STATE_ACTION.MINIMISED,
    };

    // Mark this as a user-initiated change
    isUserInitiatedRef.current = true;
    setState(newState);
  };

  const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newState = { ...state, action: BANNER_STATE_ACTION.DISMISSED };

    // Mark this as a user-initiated change
    isUserInitiatedRef.current = true;
    setState(newState);
  };

  if (state?.action === BANNER_STATE_ACTION.DISMISSED && !nonDismissable) {
    return null;
  }

  return (
    <Card
      sx={{
        width: '100%',
        cursor: 'pointer',
        backgroundColor: alpha(theme.palette[colour].light, 0.1),
        overflow: 'hidden',
        ...sx,
      }}
      onClick={handleToggleExpanded}
      {...props}
    >
      <CardContent
        sx={{
          padding: 1,
          paddingInline: 2,
          paddingBottom: state?.action === BANNER_STATE_ACTION.EXPANDED ? '1rem !important' : '0.5rem !important',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2" fontWeight="600">
              {title}
            </Typography>
          </Stack>
          <Stack direction="row">
            {!nonDismissable && (
              <IconButton disabled={!bannerId} disableRipple onClick={handleDismiss} size="small">
                <Typography variant="caption">Dismiss</Typography>
              </IconButton>
            )}
            <IconButton onClick={handleToggleExpanded} size="small">
              <KeyboardArrowDown
                style={{
                  transition: 'transform 0.1s ease-in-out',
                  transform: state?.action === BANNER_STATE_ACTION.EXPANDED ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </IconButton>
          </Stack>
        </div>
        <Collapse in={state?.action === BANNER_STATE_ACTION.EXPANDED}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              maxWidth: '100%',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {description}
          </Typography>
        </Collapse>
      </CardContent>
    </Card>
  );
};
